/**
 * ImageEditor — In-browser görsel düzenleme (crop + rotate + zoom).
 *
 * Şub 16, 2026 — react-easy-crop üzerine kurulu hafif bir editör. Kullanıcı
 * görselin bir bölgesini seçer / döndürür / yakınlaştırır. `onSave` çağrıldığında
 * seçilen alanı canvas ile render eden Blob (JPEG q0.92) döner.
 *
 * Props:
 *   src        : Görsel URL (blob URL veya harici)
 *   filename   : Kaydedilirken kullanılacak isim (uzantı önemli değil, JPEG döndürür)
 *   onSave(blob, {rotation, crop})
 *   onClose()
 *   loading    : Boolean — parent kaydediyor (kaydetme butonu disabled)
 */
import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { RotateCw, RotateCcw, ZoomIn, ZoomOut, Save, X, Crop as CropIcon } from 'lucide-react';
import { Button } from './ui/button';

async function _cropped(src, cropPixels, rotation) {
  const image = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const rad = (rotation * Math.PI) / 180;
  // Rotated bounding box
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const rotW = image.width * cos + image.height * sin;
  const rotH = image.width * sin + image.height * cos;
  // Kaynak rotate + crop için ara canvas
  const rc = document.createElement('canvas');
  rc.width = rotW; rc.height = rotH;
  const rctx = rc.getContext('2d');
  rctx.translate(rotW / 2, rotH / 2);
  rctx.rotate(rad);
  rctx.drawImage(image, -image.width / 2, -image.height / 2);
  // Kırpma
  const out = document.createElement('canvas');
  out.width = Math.round(cropPixels.width);
  out.height = Math.round(cropPixels.height);
  const octx = out.getContext('2d');
  octx.drawImage(rc, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
                 0, 0, out.width, out.height);
  return new Promise((res) => out.toBlob((b) => res(b), 'image/jpeg', 0.92));
}

export default function ImageEditor({ src, filename, onSave, onClose, loading }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [cropAreaPixels, setCropAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(undefined); // free by default

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCropAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!cropAreaPixels) return;
    const blob = await _cropped(src, cropAreaPixels, rotation);
    if (blob) onSave(blob, { rotation, crop: cropAreaPixels });
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-2 sm:p-4" data-testid="image-editor-modal">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <CropIcon className="h-5 w-5 text-indigo-600" />
            <div className="font-semibold truncate">Görsel Düzenle: {filename}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none px-2" aria-label="Kapat">×</button>
        </div>

        {/* Cropper */}
        <div className="relative flex-1 bg-slate-900 min-h-[300px] sm:min-h-[420px]">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            restrictPosition={false}
          />
        </div>

        {/* Controls */}
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 space-y-3 shrink-0">
          {/* Aspect chips */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 mr-1">Oran:</span>
            {[
              { label: 'Serbest', value: undefined },
              { label: '1:1',   value: 1 },
              { label: '4:3',   value: 4/3 },
              { label: '3:4',   value: 3/4 },
              { label: '16:9',  value: 16/9 },
              { label: '9:16',  value: 9/16 },
            ].map((a) => (
              <button
                key={a.label}
                onClick={() => setAspect(a.value)}
                className={`text-xs px-2 py-1 rounded border transition ${aspect === a.value
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                data-testid={`editor-aspect-${a.label}`}
              >
                {a.label}
              </button>
            ))}
          </div>
          {/* Slider row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <ZoomOut className="h-4 w-4 text-slate-500" />
              <input
                type="range" min="1" max="4" step="0.05"
                value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
                data-testid="editor-zoom"
              />
              <ZoomIn className="h-4 w-4 text-slate-500" />
              <span className="text-[10px] font-mono w-10 text-right text-slate-500">{zoom.toFixed(2)}x</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setRotation((r) => (r - 90) % 360)}
                className="h-8 w-8 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center"
                data-testid="editor-rotate-ccw"
                title="Sola döndür"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <input
                type="range" min="-180" max="180" step="1"
                value={rotation} onChange={(e) => setRotation(Number(e.target.value))}
                className="flex-1"
                data-testid="editor-rotation"
              />
              <button
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="h-8 w-8 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center"
                data-testid="editor-rotate-cw"
                title="Sağa döndür"
              >
                <RotateCw className="h-4 w-4" />
              </button>
              <span className="text-[10px] font-mono w-10 text-right text-slate-500">{rotation}°</span>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="editor-cancel-btn">
              <X className="h-3.5 w-3.5 mr-1" /> İptal
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={loading || !cropAreaPixels}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="editor-save-btn"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {loading ? 'Kaydediliyor…' : 'Kaydet ve Yükle'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
